import React, { useState, useEffect } from 'react';
import { DataProviderInstance } from '../../api/DataProvider';
import { Pagination, Skeleton, Tooltip } from '@mui/material';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { USER_ROLES } from '../../constants/userRoles';
import {
    Add as AddIcon,
    PaymentOutlined as PaymentOutlinedIcon,
    AccountBalanceWalletOutlined as WalletIcon,
    CreditCardOutlined as CardIcon,
} from '@mui/icons-material';
import PopupBase from '../../popup/base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import ConfirmPopup from '../../popup/ConfirmPopup/ConfirmPopup';
import AreaTitle from '../../components/areaTitle/AreaTitle';
import PaymentMethodRow from './components/PaymentMethodRow';
import PaymentMethodForm from './components/PaymentMethodForm';
import { usePaymentMethodForm } from './components/usePaymentMethodForm';
import Toast from '../support/Toast';
import styles from './PaymentMethodList.module.css';

const ADD_DENIED = 'Apenas proprietários e administradores podem adicionar métodos de pagamento';

/** Botão "Adicionar método" com o motivo no tooltip quando a pessoa não pode mexer. */
function AddMethodButton({ canManage, onClick }) {
    return (
        <Tooltip title={!canManage ? ADD_DENIED : ''} arrow>
            <span>
                <CustomButton
                    style="main"
                    variant="contained"
                    icon={<AddIcon />}
                    label="Adicionar método"
                    onClick={onClick}
                    disabled={!canManage}
                />
            </span>
        </Tooltip>
    );
}

function LoadingRows() {
    return (
        <ul className={styles.list} aria-busy="true">
            {[0, 1, 2].map((i) => (
                <li key={i} className={styles.skeletonRow}>
                    <Skeleton variant="rounded" width={40} height={40} sx={{ borderRadius: '11px', bgcolor: '#EEF1F8' }} />
                    <Skeleton variant="text" width="40%" sx={{ bgcolor: '#EEF1F8' }} />
                    <Skeleton variant="rounded" width={90} height={22} sx={{ borderRadius: '999px', bgcolor: '#EEF1F8' }} />
                </li>
            ))}
        </ul>
    );
}

function EmptyMethods({ canManage, onAdd }) {
    return (
        <div className={styles.empty}>
            <span className={styles.emptyArt}>
                <span className={styles.emptyArtBack}><CardIcon /></span>
                <span className={styles.emptyArtFront}><WalletIcon /></span>
            </span>
            <strong className={styles.emptyTitle}>Nenhum método de pagamento cadastrado</strong>
            <span className={styles.emptyText}>Cadastre Pix, cartão, dinheiro ou o que você aceita.</span>
            <AddMethodButton canManage={canManage} onClick={onAdd} />
        </div>
    );
}

export const PaymentMethodList = () => {
    const { CompanyConfig, GetCompanyPaymentMethods, SetCompanyPaymentMethod, UpdateCompanyPaymentMethod, DeleteCompanyPaymentMethod } = DataProviderInstance();
    const { hasRole } = useRoleAccess();
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [methodToDelete, setMethodToDelete] = useState(null);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    // Verificar se o usuário tem permissão para adicionar/editar/excluir métodos de pagamento (COMPANY_OWNER ou COMPANY_ADMIN)
    const canManagePaymentMethods = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);
    // Nome, ajuste e validação do popup (compartilhados com o faturamento da agenda)
    const form = usePaymentMethodForm();
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        loadPaymentMethods();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadPaymentMethods = () => {
        GetCompanyPaymentMethods((result) => {
            setPaymentMethods(result || []);
            setLoading(false);
        });
    };

    // Criar, editar e excluir já buscam a lista de novo no DataProvider (PaymentOptions):
    // a tela só acompanha essa lista, sem uma segunda chamada
    const syncedOptions = CompanyConfig?.PaymentOptions;
    useEffect(() => {
        if (!loading && Array.isArray(syncedOptions)) setPaymentMethods(syncedOptions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [syncedOptions]);

    const showToast = (message, severity = 'success') => {
        setToast({ open: true, message, severity });
    };

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleOpenModal = (mode, method = null) => {
        setModalMode(mode);
        setSelectedMethod(method);
        form.reset(method);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedMethod(null);
        form.reset();
    };

    const handleSaved = (result, successMessage) => {
        if (result?.error) {
            showToast('Não foi possível salvar o método. Tente novamente.', 'error');
            return;
        }
        handleCloseModal();
        showToast(successMessage);
    };

    const handleSubmit = () => {
        if (!form.validate()) return;
        const payload = form.payload();
        if (modalMode == 'create') {
            SetCompanyPaymentMethod(payload, (result) => handleSaved(result, 'Método adicionado'));
        } else {
            UpdateCompanyPaymentMethod({
                ...payload,
                id: selectedMethod.Id
            }, (result) => handleSaved(result, 'Método atualizado'));
        }
    };

    const handleOpenDeleteModal = (method) => {
        setMethodToDelete(method);
        setShowConfirmDelete(true);
    };

    const handleCloseDeleteModal = () => {
        setShowConfirmDelete(false);
        setMethodToDelete(null);
    };

    const handleDelete = () => {
        if (methodToDelete) {
            DeleteCompanyPaymentMethod(methodToDelete.Id, (result) => {
                handleCloseDeleteModal();
                if (result?.error) {
                    showToast('Não foi possível excluir o método. Tente novamente.', 'error');
                } else {
                    showToast('Método excluído');
                }
            });
        }
    };

    // Calculate pagination
    const totalPages = Math.ceil(paymentMethods.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = paymentMethods.slice(indexOfFirstItem, indexOfLastItem);
    const count = paymentMethods.length;

    const renderBody = () => {
        if (loading) return <LoadingRows />;
        if (currentItems.length === 0) {
            return <EmptyMethods canManage={canManagePaymentMethods} onAdd={() => handleOpenModal('create')} />;
        }
        return (
            <ul className={styles.list}>
                {currentItems.map(method => (
                    <PaymentMethodRow
                        key={method.Id}
                        method={method}
                        canManage={canManagePaymentMethods}
                        onEdit={(m) => handleOpenModal('edit', m)}
                        onDelete={handleOpenDeleteModal}
                    />
                ))}
            </ul>
        );
    };

    return (
        <div className="page-content">
            <div className={styles.page}>
                <div className={styles.headerContainer}>
                    <AreaTitle
                        Title="Métodos de Pagamento"
                        Description="As formas de pagamento que você aceita e o ajuste de preço de cada uma."
                        Icon={<PaymentOutlinedIcon />}
                    />
                    <AddMethodButton canManage={canManagePaymentMethods} onClick={() => handleOpenModal('create')} />
                </div>

                <section className={styles.card}>
                    <header className={styles.cardHeader}>
                        <h3 className={styles.cardTitle}>
                            Métodos{!loading && count > 0 ? ` · ${count}` : ''}
                        </h3>
                        {!loading && count > 0 && (
                            <span className={styles.legend}>
                                <span className={`${styles.legendDot} ${styles.dotDiscount}`} />Desconto
                                <span className={`${styles.legendDot} ${styles.dotIncrease}`} />Acréscimo
                            </span>
                        )}
                    </header>

                    {renderBody()}

                    {!loading && count > 0 && (
                        <footer className={styles.cardFooter}>
                            <span className={styles.paginationText}>
                                Página {currentPage} de {totalPages || 1} ({count} {count === 1 ? 'método' : 'métodos'} de pagamento)
                            </span>
                            {totalPages > 1 && (
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    shape="rounded"
                                    size="small"
                                    sx={{
                                        '& .MuiPaginationItem-root': { color: '#2D3748' },
                                        '& .Mui-selected': { backgroundColor: '#5A80FB !important', color: '#FFFFFF' },
                                        '& .MuiPaginationItem-root:hover': { backgroundColor: 'rgba(90, 128, 251, 0.1)' },
                                    }}
                                />
                            )}
                        </footer>
                    )}
                </section>
            </div>

            {isModalOpen && (
                <PopupBase
                    Title={modalMode == 'create' ? 'Novo método de pagamento' : 'Editar método de pagamento'}
                    Content={<PaymentMethodForm formData={form.formData} onChange={form.handleChange} errors={form.errors} />}
                    maxWidth="sm"
                    PositiveButton={
                        <CustomButton
                            style="main"
                            variant="contained"
                            label={modalMode == 'create' ? 'Adicionar' : 'Salvar'}
                            onClick={handleSubmit}
                        />
                    }
                    NegativeButton={
                        <CustomButton
                            style="primary"
                            variant="text"
                            label="Cancelar"
                            onClick={handleCloseModal}
                        />
                    }
                    OnClose={handleCloseModal}
                />
            )}

            <ConfirmPopup
                Visibility={showConfirmDelete}
                Title="Excluir Método de Pagamento"
                Description={`Tem certeza que deseja excluir o método de pagamento "${methodToDelete?.Label}"?`}
                ConfirmLabel="Excluir"
                OnConfirm={handleDelete}
                OnHide={handleCloseDeleteModal}
            />

            <Toast
                open={toast.open}
                onClose={() => setToast({ ...toast, open: false })}
                message={toast.message}
                severity={toast.severity}
                autoHideDuration={6000}
            />
        </div>
    );
};
